import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import E8TrapezoidPartitionDiagram from "../../../../components/diagrams/E8TrapezoidPartitionDiagram";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function buildData({ angleABC, angleBCE }) {
  const angleDAB = 180 - angleABC - angleBCE;
  const angleBCD = 180 - angleABC;
  const angleCDA = 180 - angleDAB;

  return {
    angleABC,
    angleBCE,
    angleDAB,
    angleBCD,
    angleCDA,
    maxPoints: 2,
  };
}

function createOriginalTask() {
  return {
    type: "E8TrapezoidAnglesOpenTask",
    data: buildData({
      angleABC: 48,
      angleBCE: 57,
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 18,
    },
    tags: ["e8", "planimetria", "czworokaty", "trapezy", "katy", "zadanie-otwarte"],
  };
}

const RANDOM_ANGLE_PAIRS = [
  { angleABC: 42, angleBCE: 58 },
  { angleABC: 45, angleBCE: 60 },
  { angleABC: 50, angleBCE: 55 },
  { angleABC: 52, angleBCE: 48 },
  { angleABC: 54, angleBCE: 51 },
  { angleABC: 56, angleBCE: 49 },
  { angleABC: 60, angleBCE: 45 },
  { angleABC: 62, angleBCE: 43 },
];

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const pair = pick(RANDOM_ANGLE_PAIRS);
  return {
    type: "E8TrapezoidAnglesOpenTask",
    data: buildData(pair),
    source: { variant: "random", basedOn: "E8_2025_MAJ_18" },
    tags: ["e8", "planimetria", "czworokaty", "trapezy", "katy", "zadanie-otwarte"],
  };
}

function getRaw(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  if (typeof value.value === "string") return value.value;
  if (typeof value.parsed === "number") return String(value.parsed);
  return "";
}

function parseAngle(value) {
  const raw = getRaw(value)
    .trim()
    .replace(",", ".")
    .replace(/[°\s]/g, "");
  if (!raw) return null;
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return null;
  return Math.round(numeric * 100) / 100;
}

function isAngleCorrect(value, expected) {
  const parsed = parseAngle(value);
  return parsed != null && Math.abs(parsed - expected) < 0.001;
}

function AngleInputRow({ label, value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ opacity: 0.9 }}>
        <InlineMath math={`|\\angle ${label}| =`} />
      </span>
      <FractionInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={`|\\angle ${label}|`}
        suffixLatex={"^{\\circ}"}
        placeholder="np. 75"
      />
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <p>
        Na rysunku przedstawiono trapez <InlineMath math="ABCD" />, w którym kąt{" "}
        <InlineMath math="ABC" /> ma miarę <InlineMath math={`${data.angleABC}^{\\circ}`} />.
        Odcinek <InlineMath math="EC" /> dzieli ten trapez na równoległobok{" "}
        <InlineMath math="AECD" /> i trójkąt <InlineMath math="EBC" />, w którym kąt{" "}
        <InlineMath math="BCE" /> ma miarę <InlineMath math={`${data.angleBCE}^{\\circ}`} />.
      </p>
      <E8TrapezoidPartitionDiagram angleABC={data.angleABC} angleBCE={data.angleBCE} />
      <p>
        Oblicz miary kątów <InlineMath math="DAB" />, <InlineMath math="BCD" /> i{" "}
        <InlineMath math="CDA" /> trapezu <InlineMath math="ABCD" />.
      </p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left", marginTop: 10 }}>
      <div
        style={{
          fontSize: 12.5,
          opacity: 0.78,
          lineHeight: 1.45,
          borderLeft: "3px solid var(--color-accent)",
          paddingLeft: 8,
        }}
      >
        <strong>{"Wpisz wszystkie trzy miary k\u0105t\u00f3w."}</strong>{" "}
        {"Za trzy poprawne warto\u015bci otrzymujesz 2 punkty, za dwie poprawne warto\u015bci 1 punkt."}
      </div>
      <AngleInputRow label="DAB" value={userAnswer.dab} onChange={setField("dab")} disabled={disabled} />
      <AngleInputRow label="BCD" value={userAnswer.bcd} onChange={setField("bcd")} disabled={disabled} />
      <AngleInputRow label="CDA" value={userAnswer.cda} onChange={setField("cda")} disabled={disabled} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const parts = {
    "kąt DAB": isAngleCorrect(userAnswer.dab, data.angleDAB),
    "kąt BCD": isAngleCorrect(userAnswer.bcd, data.angleBCD),
    "kąt CDA": isAngleCorrect(userAnswer.cda, data.angleCDA),
  };
  const correctCount = Object.values(parts).filter(Boolean).length;
  const score = correctCount === 3 ? 2 : correctCount >= 2 ? 1 : 0;
  return {
    isCorrect: score === data.maxPoints,
    score,
    maxScore: data.maxPoints,
    parts,
  };
}

export function renderAnswer({ data }) {
  return (
    <span>
      <InlineMath math={`|\\angle DAB|=${data.angleDAB}^{\\circ}`} />,{" "}
      <InlineMath math={`|\\angle BCD|=${data.angleBCD}^{\\circ}`} />,{" "}
      <InlineMath math={`|\\angle CDA|=${data.angleCDA}^{\\circ}`} />
    </span>
  );
}

function angleAnswerLatex(value) {
  const parsed = parseAngle(value);
  return parsed == null ? "\\text{brak}" : `${parsed}^{\\circ}`;
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div><InlineMath math={`|\\angle DAB|=${angleAnswerLatex(userAnswer.dab)}`} /></div>
      <div><InlineMath math={`|\\angle BCD|=${angleAnswerLatex(userAnswer.bcd)}`} /></div>
      <div><InlineMath math={`|\\angle CDA|=${angleAnswerLatex(userAnswer.cda)}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Najpierw korzystamy z sumy kątów w trójkącie{" "}
        <InlineMath math="EBC" />. Znamy dwa kąty: <InlineMath math={`ABC=${data.angleABC}^{\\circ}`} /> oraz{" "}
        <InlineMath math={`BCE=${data.angleBCE}^{\\circ}`} />.
      </p>
      <BlockMath math={`|\\angle CEB|=180^{\\circ}-${data.angleABC}^{\\circ}-${data.angleBCE}^{\\circ}=${data.angleDAB}^{\\circ}`} />
      <p>
        <strong>Krok 2.</strong> Czworokąt <InlineMath math="AECD" /> jest równoległobokiem,
        więc <InlineMath math={"AD\\parallel EC"} /> oraz <InlineMath math={"AB\\parallel DC"} />.
        Dlatego kąt <InlineMath math="DAB" /> ma taką samą miarę jak kąt <InlineMath math="CEB" />.
      </p>
      <BlockMath math={`|\\angle DAB|=${data.angleDAB}^{\\circ}`} />
      <p>
        <strong>Krok 3.</strong> Podstawy trapezu są równoległe. Kąty przy ramieniu{" "}
        <InlineMath math="BC" /> uzupełniają się do <InlineMath math={"180^{\\circ}"} />.
      </p>
      <BlockMath math={`|\\angle BCD|=180^{\\circ}-${data.angleABC}^{\\circ}=${data.angleBCD}^{\\circ}`} />
      <p>
        <strong>Krok 4.</strong> W równoległoboku kąty leżące przy jednym boku też
        uzupełniają się do <InlineMath math={"180^{\\circ}"} />.
      </p>
      <BlockMath math={`|\\angle CDA|=180^{\\circ}-${data.angleDAB}^{\\circ}=${data.angleCDA}^{\\circ}`} />
    </div>
  );
}
